import React, { useCallback, useEffect, useState } from 'react';

import { transportStatementsAPI } from '../../lib/inboundRentalsApi';
import { messageErreur } from '../../lib/erreurs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';

/**
 * Premier numéro d'état de ce transporteur (le « 5 » de « BPS F005-PKS »), à
 * fixer une fois par l'administrateur. Il se verrouille dès qu'un état est
 * numéroté. Se place dans les conditions du contrat, à côté du sigle.
 */
const NumerotationEtats = ({ contractId, disabled = false, onChanged }) => {
  const [numerotation, setNumerotation] = useState(null);
  const [valeur, setValeur] = useState('');
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const liste = await transportStatementsAPI.list(contractId);
      const numbering = liste?.numbering || null;
      setNumerotation(numbering);
      setValeur(String(numbering?.statement_start_number ?? numbering?.next_number ?? 1));
    } catch {
      setNumerotation(null);
    }
  }, [contractId]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!numerotation) return null;

  const verrouille = Boolean(numerotation.locked);
  const nombre = Number(valeur);
  const valide = Number.isInteger(nombre) && nombre >= 1;
  const inchange = Number(numerotation.statement_start_number) === nombre;

  const fixer = async () => {
    setBusy(true);
    try {
      await transportStatementsAPI.setNumbering(contractId, nombre);
      toast.success('Premier numéro d’état enregistré');
      await charger();
      if (onChanged) await onChanged();
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement du premier numéro impossible'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="numerotation-etats">
      <Label htmlFor="etats-premier-numero">Premier numéro d’état</Label>
      <div className="flex items-center gap-2">
        <Input
          id="etats-premier-numero"
          type="number"
          min="1"
          step="1"
          inputMode="numeric"
          value={valeur}
          disabled={disabled || verrouille}
          onChange={(event) => setValeur(event.target.value)}
          className="w-28 text-right tabular-nums"
        />
        {!verrouille ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy || !valide || inchange}
            onClick={fixer}
            data-testid="fixer-premier-numero"
          >
            {busy ? 'Enregistrement…' : 'Fixer'}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default NumerotationEtats;
